import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Filter, 
  Download,
  Search,
  Eye,
  RefreshCw
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface Commission {
  _id: string;
  affiliateId: string;
  userId: {
    name: string;
    email: string;
    phone?: string;
  };
  paymentId: string;
  planAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  createdAt: string;
  notes?: string;
  wordpressSite?: {
    siteName: string;
    wordpressUrl: string;
  };
}

interface CommissionResponse {
  success: boolean;
  data: {
    commissions: Commission[];
    pagination: {
      total: number;
      page: number;
      pages: number;
      limit: number;
    };
    summary: {
      totalAmount: number;
      count: number;
    };
  };
}

const fetchCommissions = async (params: any): Promise<CommissionResponse> => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/admin/affiliates/commissions`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return data;
};

export function AdminAffiliateCommissions() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: 'pending',
    page: 1,
    limit: 20,
    search: '',
    affiliateId: ''
  });

  const { data: commissionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['affiliate-commissions', filters],
    queryFn: () => fetchCommissions(filters),
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false
  });

  const approveMutation = useMutation({
    mutationFn: async (commissionId: string) => {
      const token = localStorage.getItem('token');
      return axios.put(
        `${API_URL}/admin/affiliates/commissions/${commissionId}/approve`,
        { notes: 'Approved by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success('Commission approved successfully');
      queryClient.invalidateQueries({ queryKey: ['affiliate-commissions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to approve commission');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ commissionId, reason }: { commissionId: string; reason: string }) => {
      const token = localStorage.getItem('token');
      return axios.put(
        `${API_URL}/admin/affiliates/commissions/${commissionId}/reject`,
        { notes: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success('Commission rejected');
      queryClient.invalidateQueries({ queryKey: ['affiliate-commissions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject commission');
    }
  });

  const payMutation = useMutation({
    mutationFn: async (commissionId: string) => {
      const token = localStorage.getItem('token');
      return axios.put(
        `${API_URL}/admin/affiliates/commissions/${commissionId}/pay`,
        { 
          paymentMethod: 'wallet_credit',
          notes: 'Paid via wallet credit' 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success('Commission paid successfully');
      queryClient.invalidateQueries({ queryKey: ['affiliate-commissions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to pay commission');
    }
  });

  const handleApprove = (commissionId: string) => {
    if (confirm('Are you sure you want to approve this commission?')) {
      approveMutation.mutate(commissionId);
    }
  };

  const handleReject = (commissionId: string) => {
    const reason = prompt('Please enter rejection reason:');
    if (reason) {
      rejectMutation.mutate({ commissionId, reason });
    }
  };

  const handlePay = (commissionId: string) => {
    if (confirm('Are you sure you want to mark this commission as paid? This will credit the amount to the affiliate wallet.')) {
      payMutation.mutate(commissionId);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500 text-white"><Clock className="size-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="size-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white"><XCircle className="size-3 mr-1" /> Rejected</Badge>;
      case 'paid':
        return <Badge className="bg-blue-500 text-white"><DollarSign className="size-3 mr-1" /> Paid</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-gray-900">Affiliate Commissions</h1>
          <p className="text-gray-600 mt-1">Manage affiliate earnings and payouts</p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="size-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="p-8 bg-red-50 border-red-200">
          <div className="flex items-center justify-center flex-col">
            <XCircle className="size-12 text-red-500 mb-4" />
            <h3 className="text-xl text-red-900 mb-2">Backend Connection Error</h3>
            <p className="text-red-700 text-center mb-4">
              Unable to connect to the backend server. Please ensure your backend is running.
            </p>
            <div className="bg-red-100 p-4 rounded-lg mb-4 text-sm text-red-800">
              <p className="mb-2">Current API URL: <code className="bg-red-200 px-2 py-1 rounded">{API_URL}</code></p>
              <p>To fix this:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Set <code className="bg-red-200 px-1 rounded">VITE_BACKEND_URL</code> environment variable</li>
                <li>Or set backend URL in localStorage: <code className="bg-red-200 px-1 rounded">localStorage.setItem('BACKEND_URL', 'your-url')</code></li>
                <li>Make sure your Node.js backend server is running</li>
              </ul>
            </div>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="size-4 mr-2" />
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      {!error && commissionsData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Total Amount</p>
                <p className="text-2xl text-yellow-900 mt-1">
                  ₹{commissionsData.data.summary.totalAmount.toLocaleString()}
                </p>
              </div>
              <DollarSign className="size-10 text-yellow-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Total Count</p>
                <p className="text-2xl text-blue-900 mt-1">
                  {commissionsData.data.summary.count}
                </p>
              </div>
              <CheckCircle className="size-10 text-blue-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Average Commission</p>
                <p className="text-2xl text-green-900 mt-1">
                  ₹{commissionsData.data.summary.count > 0 
                    ? (commissionsData.data.summary.totalAmount / commissionsData.data.summary.count).toFixed(2)
                    : 0}
                </p>
              </div>
              <DollarSign className="size-10 text-green-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Current Page</p>
                <p className="text-2xl text-purple-900 mt-1">
                  {commissionsData.data.pagination.page} / {commissionsData.data.pagination.pages}
                </p>
              </div>
              <Eye className="size-10 text-purple-600 opacity-50" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm text-gray-600 mb-2 block">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-2 block">Affiliate ID</label>
            <Input
              placeholder="Filter by affiliate ID"
              value={filters.affiliateId}
              onChange={(e) => setFilters({ ...filters, affiliateId: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-2 block">Per Page</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value), page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="flex items-end">
            <Button 
              onClick={() => setFilters({ status: 'pending', page: 1, limit: 20, search: '', affiliateId: '' })}
              variant="outline"
              className="w-full"
            >
              <Filter className="size-4 mr-2" />
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Commissions Table */}
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm text-gray-600">Affiliate Site</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Customer</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Plan Amount</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Commission</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Rate</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-sm text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="size-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading commissions...</span>
                    </div>
                  </td>
                </tr>
              ) : commissionsData && commissionsData.data.commissions.length > 0 ? (
                commissionsData.data.commissions.map((commission) => (
                  <tr key={commission._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm text-gray-900">
                          {commission.wordpressSite?.siteName || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {commission.affiliateId}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm text-gray-900">{commission.userId.name}</p>
                        <p className="text-xs text-gray-500">{commission.userId.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">₹{commission.planAmount.toLocaleString()}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-green-600">
                        ₹{commission.commissionAmount.toLocaleString()}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">{(commission.commissionRate * 100).toFixed(1)}%</p>
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(commission.status)}
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">
                        {new Date(commission.createdAt).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {commission.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(commission._id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="size-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(commission._id)}
                            >
                              <XCircle className="size-4" />
                            </Button>
                          </>
                        )}
                        {commission.status === 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => handlePay(commission._id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <DollarSign className="size-4 mr-1" />
                            Pay
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    No commissions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {commissionsData && commissionsData.data.pagination.pages > 1 && (
          <PaginationWrapper
            currentPage={filters.page}
            totalPages={commissionsData.data.pagination.pages}
            totalItems={commissionsData.data.pagination.total}
            itemsPerPage={filters.limit}
            onPageChange={(page) => setFilters({ ...filters, page })}
            onItemsPerPageChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
          />
        )}
      </Card>
    </div>
  );
}